import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Globe, 
  Cpu, 
  BarChart3, 
  Workflow, 
  Zap, 
  Shield, 
  Rocket, 
  CheckCircle2,
  ArrowRight,
  Code,
  Database,
  Layers,
  Monitor
} from "lucide-react";
import ParticleBackground from "@/components/ParticleBackground";
import SEOHead from "@/components/SEOHead";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_data-xm-dev/artifacts/nfhnqfv8_640AB213-1DF7-4C40-9AAA-5ECCBB333955.png";

// Optimized Neon Flare Component with will-change for GPU acceleration
const NeonFlare = ({ delay, left, size = "normal" }) => {
  const height = size === "large" ? "h-40" : size === "medium" ? "h-28" : "h-20";
  const width = size === "large" ? "w-1.5" : "w-1";
  
  return (
    <motion.div
      initial={{ y: "100vh", opacity: 0 }}
      animate={{ 
        y: "-100vh", 
        opacity: [0, 0.9, 0.6, 0.3, 0] 
      }}
      transition={{ 
        duration: 6 + Math.random() * 2,
        delay: delay,
        repeat: Infinity,
        ease: "linear"
      }}
      className="absolute pointer-events-none"
      style={{ 
        left: `${left}%`,
        willChange: "transform, opacity",
        transform: "translateZ(0)"
      }}
    >
      <div 
        className={`${width} ${height} rounded-full`}
        style={{
          background: "linear-gradient(to top, transparent, rgba(59, 130, 246, 0.8), rgba(147, 197, 253, 0.6), transparent)",
          boxShadow: "0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(59, 130, 246, 0.3)",
        }}
      />
    </motion.div>
  );
};

// Optimized Flares Container - fewer flares for better performance
const NeonFlaresEffect = () => {
  const flares = [];
  
  // Reduced to 15 flares for smoother performance
  for (let i = 0; i < 15; i++) {
    const size = i % 4 === 0 ? "large" : i % 2 === 0 ? "medium" : "normal";
    flares.push({
      id: i,
      delay: i * 0.5,
      left: 5 + (i * 6) + Math.random() * 2,
      size
    });
  }

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]" style={{ willChange: "contents" }}>
      {flares.map((flare) => (
        <NeonFlare key={flare.id} delay={flare.delay} left={flare.left} size={flare.size} />
      ))}
    </div>
  );
};

const services = [
  {
    id: "web",
    icon: Globe,
    title: "Website Development",
    description: "Professional websites and landing pages designed for performance and lead generation.",
    image: "https://images.unsplash.com/photo-1735713212083-82eafc42bf64?w=800&q=80",
    color: "#2563EB",
    features: ["Landing Pages", "Business Sites", "Mobile-First", "SEO Ready"],
  },
  {
    id: "workflow",
    icon: Workflow,
    title: "Custom Workflow Apps",
    description: "We build CRMs, internal platforms, dashboards, and workflow automation tools tailored for businesses.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
    color: "#7C3AED",
    features: ["Custom CRMs", "Dashboards", "Automation", "Integrations"],
  },
  {
    id: "ai",
    icon: Cpu,
    title: "AI Integration",
    description: "AI-powered automation, intelligent workflows, and modern digital systems for companies.",
    image: "https://images.unsplash.com/photo-1764336312138-14a5368a6cd3?w=800&q=80",
    color: "#3B82F6",
    features: ["AI Automation", "Smart Workflows", "Data Processing", "ML Models"],
  },
  {
    id: "marketing",
    icon: BarChart3,
    title: "Marketing Technology",
    description: "Analytics, tracking systems, conversion optimization and automation tools.",
    image: "https://images.unsplash.com/photo-1752253604157-65fb42c30816?w=800&q=80",
    color: "#8B5CF6",
    features: ["Analytics", "Tracking", "Conversion", "Automation"],
  },
];

const benefits = [
  {
    icon: CheckCircle2,
    text: "Custom-built solutions tailored to each business",
  },
  {
    icon: Zap,
    text: "Modern AI and workflow automation integration",
  },
  {
    icon: Rocket,
    text: "Fast development and scalable infrastructure",
  },
  {
    icon: Shield,
    text: "Clean design and high-performance websites",
  },
];

export const HomePage = () => {
  const [activeService, setActiveService] = useState(0);

  return (
    <>
      <SEOHead 
        title="Global Data XM | Custom Digital Solutions for Modern Businesses"
        description="Web Development, AI Solutions, Workflow Apps and Marketing Technology designed to help companies scale faster."
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800" data-testid="hero-section">
        <ParticleBackground />
        
        {/* Neon Flares Effect */}
        <NeonFlaresEffect />
        
        {/* Gradient Orbs */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-600/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-violet-600/20 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[150px]" />
        
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10 pt-24">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Logo - BIGGER */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-10 relative"
            >
              {/* Logo glow effect */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 bg-blue-500/20 rounded-full blur-[80px] animate-pulse" />
              </div>
              <img 
                src={LOGO_URL} 
                alt="Global Data XM" 
                className="h-48 md:h-64 lg:h-80 xl:h-96 w-auto mx-auto drop-shadow-2xl relative z-10"
                style={{
                  filter: "drop-shadow(0 0 30px rgba(59, 130, 246, 0.4)) drop-shadow(0 0 60px rgba(124, 58, 237, 0.3))"
                }}
                data-testid="hero-logo"
              />
            </motion.div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 leading-tight" data-testid="hero-headline">
              Custom Digital Solutions for{" "}
              <span className="bg-gradient-to-r from-blue-400 via-violet-400 to-purple-400 bg-clip-text text-transparent">
                Modern Businesses
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed">
              Web Development, AI Solutions, Workflow Apps and Marketing Technology designed to help companies scale faster.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="group relative bg-gradient-to-r from-blue-600 to-violet-600 text-white px-8 py-4 rounded-full font-medium text-base overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] hover:scale-105"
                data-testid="hero-cta-primary"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Start a Project
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
              <Link
                to="/services"
                className="bg-white/10 backdrop-blur-sm text-white border border-white/20 px-8 py-4 rounded-full font-medium text-base hover:bg-white/20 transition-all hover:border-white/40"
                data-testid="hero-cta-secondary"
              >
                Explore Services
              </Link>
            </div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2"
          >
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="w-1.5 h-3 bg-white/50 rounded-full mt-2"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Interactive Services Section */}
      <section className="py-24 bg-slate-900 relative overflow-hidden" data-testid="services-section">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-800 to-slate-900" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-violet-600/10 rounded-full blur-[120px]" />
        
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-blue-400 uppercase tracking-wider">What We Do</span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-3">
              Our Core Services
            </h2>
          </motion.div>

          {/* Interactive Service Cards */}
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Service Tabs */}
            <div className="space-y-4">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setActiveService(index)}
                  className={`cursor-pointer p-6 rounded-2xl border transition-all duration-300 ${
                    activeService === index
                      ? "bg-gradient-to-r from-blue-600/20 to-violet-600/20 border-blue-500/50 shadow-[0_0_30px_rgba(59,130,246,0.2)]"
                      : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                  }`}
                  data-testid={`service-tab-${service.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div 
                      className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all ${
                        activeService === index
                          ? "bg-gradient-to-r from-blue-500 to-violet-500"
                          : "bg-white/10"
                      }`}
                    >
                      <service.icon size={26} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white mb-2">{service.title}</h3>
                      <p className="text-slate-300 text-sm leading-relaxed">{service.description}</p>
                      
                      {/* Feature tags */}
                      <AnimatePresence>
                        {activeService === index && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="flex flex-wrap gap-2 mt-4"
                          >
                            {service.features.map((feature, fIndex) => (
                              <span
                                key={fIndex}
                                className="px-3 py-1 bg-white/10 text-xs text-white/80 rounded-full"
                              >
                                {feature}
                              </span>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Service Visual */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative aspect-square max-w-lg mx-auto">
                {/* Animated rings */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="absolute w-full h-full border border-blue-500/20 rounded-full"
                  />
                  <motion.div
                    animate={{ rotate: -360 }}
                    transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                    className="absolute w-[85%] h-[85%] border border-violet-500/20 rounded-full"
                  />
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                    className="absolute w-[70%] h-[70%] border border-blue-400/20 rounded-full"
                  />
                </div>

                {/* Center image */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeService}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.4 }}
                    className="absolute inset-0 flex items-center justify-center p-12"
                  >
                    <div className="relative w-full h-full rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                      <img
                        src={services[activeService].image}
                        alt={services[activeService].title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: services[activeService].color }}
                          />
                          <span className="text-white font-medium text-sm">
                            {services[activeService].title}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>

                {/* Floating icons */}
                <motion.div
                  animate={{ y: [-10, 10, -10] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="absolute top-8 right-8 w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg"
                >
                  <Code size={20} className="text-white" />
                </motion.div>
                <motion.div
                  animate={{ y: [10, -10, 10] }}
                  transition={{ duration: 5, repeat: Infinity }}
                  className="absolute bottom-8 left-8 w-12 h-12 bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg"
                >
                  <Database size={20} className="text-white" />
                </motion.div>
                <motion.div
                  animate={{ y: [-5, 15, -5] }}
                  transition={{ duration: 6, repeat: Infinity }}
                  className="absolute top-1/2 left-0 w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center shadow-lg"
                >
                  <Layers size={16} className="text-white" />
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* View all services link */}
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-colors"
              data-testid="view-all-services"
            >
              View All Services
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-white" data-testid="why-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium text-violet-600 uppercase tracking-wider">Why Us</span>
              <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mt-3 mb-6">
                Why Companies Work With Us
              </h2>
              <p className="text-slate-600 text-lg mb-8">
                We combine cutting-edge technology with strategic thinking to deliver solutions that drive real business results.
              </p>
              
              <div className="space-y-5">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4"
                    data-testid={`benefit-${index}`}
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-violet-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <benefit.icon size={22} className="text-white" />
                    </div>
                    <span className="text-slate-700 font-medium">{benefit.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="bg-gradient-to-r from-blue-600 to-violet-600 rounded-3xl p-1">
                <div className="bg-white rounded-3xl p-8">
                  <div className="grid grid-cols-2 gap-6">
                    {[
                      { value: "100+", label: "Projects Delivered" },
                      { value: "50+", label: "Happy Clients" },
                      { value: "5+", label: "Years Experience" },
                      { value: "24/7", label: "Support Available" },
                    ].map((stat, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-slate-50 rounded-2xl p-6 text-center hover:bg-slate-100 transition-colors"
                      >
                        <p className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                          {stat.value}
                        </p>
                        <p className="text-slate-600 text-sm mt-2">{stat.label}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-violet-600 to-purple-600 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
        
        {/* Animated orbs */}
        <motion.div
          animate={{ x: [0, 50, 0], y: [0, -30, 0] }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-full blur-2xl"
        />
        <motion.div
          animate={{ x: [0, -40, 0], y: [0, 40, 0] }}
          transition={{ duration: 12, repeat: Infinity }}
          className="absolute bottom-20 right-20 w-40 h-40 bg-white/10 rounded-full blur-2xl"
        />
        
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Ready to Build Your Next Digital Solution?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Let's discuss how we can help transform your business with modern technology.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="bg-white text-blue-600 px-8 py-4 rounded-full font-medium text-base hover:shadow-xl transition-all hover:scale-105 flex items-center gap-2"
                data-testid="cta-primary"
              >
                Start a Project
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                className="bg-white/10 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-full font-medium text-base hover:bg-white/20 transition-all"
                data-testid="cta-secondary"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
